<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-content-save</v-icon>
      {{ editMode ? 'Update Billing Record' : 'Save Billing Record' }}
    </v-card-title>

    <v-card-text>
      <!-- Save Confirmation -->
      <v-row v-if="!saved">
        <v-col cols="12">
          <!-- Compact validation indicator -->
          <v-alert
            :type="isValidForSave ? 'success' : 'warning'"
            outlined
            dense
          >
            <span v-if="isValidForSave">
              <v-icon left small>mdi-check-circle</v-icon>
              All data validated — ready to save.
            </span>
            <span v-else>
              <v-icon left small>mdi-alert-circle</v-icon>
              {{ firstInvalidMessage }}
            </span>
          </v-alert>
        </v-col>
      </v-row>
      <v-row v-if="saved">
        <v-col cols="12">
          <v-alert
            type="success"
            outlined
            class="mb-4"
          >
            <div class="text-subtitle-2">
              <v-icon left>mdi-check-circle</v-icon>
              Billing Record Saved Successfully
            </div>
            <div>Record ID: {{ savedRecordId }}</div>
            <div>Saved at: {{ formattedSaveTime }}</div>
          </v-alert>

          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="success">mdi-database-check</v-icon>
              Saved Record Details
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2">Record Information</div>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Record ID</v-list-item-title>
                        <v-list-item-subtitle>{{ savedRecordId }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Created By</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ currentBillData?.createdBy || 'operator' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Save Time</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedSaveTime }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="text-subtitle-2">Storage Information</div>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Storage Location</v-list-item-title>
                        <v-list-item-subtitle>Local Storage</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Data Integrity</v-list-item-title>
                        <v-list-item-subtitle>Validated and Verified</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Backup Status</v-list-item-title>
                        <v-list-item-subtitle>Automatically Backed Up</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error Display -->
      <v-row v-if="error && !saved">
        <v-col cols="12">
          <v-alert
            type="error"
            dismissible
            @input="error = null"
          >
            <div class="text-subtitle-2">Save Failed</div>
            <div>{{ error }}</div>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions v-if="!saved">
      <v-spacer />
      <v-btn
        color="grey"
        text
        @click="cancel"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!isValidForSave"
        @click="handleSaveClick"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ editMode ? 'Update Billing Record' : 'Save Billing Record' }}
      </v-btn>
    </v-card-actions>

    <v-card-actions v-if="saved">
      <v-spacer />
      <v-btn
        color="info"
        @click="viewRecord"
      >
        <v-icon left>mdi-eye</v-icon>
        View Record
      </v-btn>
      <v-btn
        color="success"
        @click="createNew"
      >
        <v-icon left>mdi-plus</v-icon>
        Create New Bill
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../../utils/billingMode';
import {
  calculateTimeOfUseBillingRate,
  calculateTimeOfUseTotalCalculated,
} from '../../utils/timeOfUseBilling';
import appConfig from '../../config/appConfig';

export default {
  name: 'BillSaveComponent',

  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    originalRecord: {
      type: Object,
      default: null,
    },
    updatedRecord: {
      type: Object,
      default: null,
    },
    billData: {
      type: Object,
      required: true,
    },
    calculatedBill: {
      type: Object,
      default: null,
    },
    consumptionData: {
      type: Object,
      default: null,
    },
    manualData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      saving: false,
      saved: false,
      savedRecordId: null,
      saveTime: null,
      error: null,
    };
  },

  computed: {
    // Get the appropriate data source based on mode
    currentBillData() {
      if (this.editMode && this.updatedRecord) {
        return this.updatedRecord;
      }
      return this.billData || {};
    },

    // Formatting
    formattedBillingPeriod() {
      const billData = this.currentBillData;
      if (!billData?.billingPeriod) return 'N/A';
      const start = new Date(billData.billingPeriod.startDate).toLocaleDateString();
      const end = new Date(billData.billingPeriod.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    },

    formattedTotalConsumption() {
      const billData = this.currentBillData;
      return billData?.consumptionData?.totalConsumption?.toLocaleString() || '0';
    },

    formattedKvaDemand() {
      const billData = this.currentBillData;
      return billData?.kvaDemand?.toLocaleString() || '0';
    },

    formattedKvaDemandCharges() {
      const billData = this.currentBillData;
      return this.$formatCurrency(parseFloat(billData?.kvaDemandCharges) || 0, {
        showSymbol: false,
      });
    },

    formattedCalculatedAmount() {
      const billData = this.currentBillData;
      const totalCalculated = this.shouldUseBillingUnitCost
        ? calculateTimeOfUseTotalCalculated(billData)
        : (parseFloat(billData?.calculatedCharges?.totalCalculated) || 0);
      return this.$formatCurrency(totalCalculated, { showSymbol: false });
    },

    usesCalculatedDemandCharge() {
      const calculatedCharges = this.currentBillData?.calculatedCharges || {};
      return Object.prototype.hasOwnProperty.call(calculatedCharges, 'KVA_DEMAND_CHARGE')
        || Object.prototype.hasOwnProperty.call(calculatedCharges, 'KVA_PER_UNIT_CHARGE');
    },

    showsManualKvaDemandCharges() {
      return !this.usesCalculatedDemandCharge && (this.currentBillData?.kvaDemandCharges > 0);
    },

    billingMode() {
      return resolveBillingMode({
        record: this.originalRecord,
        billData: this.currentBillData,
        consumptionData: this.consumptionData,
        manualData: this.manualData,
        calculatedBill: this.calculatedBill,
      });
    },

    shouldUseBillingUnitCost() {
      return appConfig.getBillingCapabilities().useTouUnitCost === true
        && isTimeOfUseBillingMode(this.billingMode);
    },

    isTimeOfUseMode() {
      return isTimeOfUseBillingMode(this.billingMode);
    },

    formattedUnitCostForBilling() {
      if (!this.shouldUseBillingUnitCost) {
        return this.$formatCurrency(0, { showSymbol: false });
      }

      return this.$formatCurrency(
        calculateTimeOfUseBillingRate(this.currentBillData),
        { showSymbol: false },
      );
    },

    formattedActualAmount() {
      const billData = this.currentBillData;
      return this.$formatCurrency(parseFloat(billData?.actualBillAmount) || 0, {
        showSymbol: false,
      });
    },

    formattedVariance() {
      const billData = this.currentBillData;
      const variance = Math.abs(billData?.variance || 0);
      return this.$formatCurrency(variance, { showSymbol: false });
    },

    formattedSaveTime() {
      return this.saveTime ? this.saveTime.toLocaleString() : 'N/A';
    },

    // Variance calculations
    variancePercentage() {
      const billData = this.currentBillData;
      if (!billData?.calculatedCharges?.totalCalculated) return '0.00';
      const variance = Math.abs(billData?.variance || 0);
      const percentage = (variance / billData.calculatedCharges.totalCalculated) * 100;
      return percentage.toFixed(2);
    },

    varianceIcon() {
      const billData = this.currentBillData;
      return (billData?.variance || 0) >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    varianceIconColor() {
      const billData = this.currentBillData;
      return (billData?.variance || 0) >= 0 ? 'error' : 'warning';
    },

    varianceTextColor() {
      const billData = this.currentBillData;
      return (billData?.variance || 0) >= 0 ? 'error--text' : 'warning--text';
    },

    // Validation
    validationResults() {
      const results = [];
      const billData = this.currentBillData;

      // Billing period validation
      if (billData?.billingPeriod
      && billData.billingPeriod.startDate
      && billData.billingPeriod.endDate) {
        results.push({
          field: 'Billing Period',
          isValid: true,
          message: 'Valid date range provided',
        });
      } else {
        results.push({
          field: 'Billing Period',
          isValid: false,
          message: 'Missing or invalid billing period',
        });
      }

      // Consumption data validation
      if (billData?.consumptionData && billData.consumptionData.totalConsumption > 0) {
        results.push({
          field: 'Consumption Data',
          isValid: true,
          message: 'Valid consumption data available',
        });
      } else {
        results.push({
          field: 'Consumption Data',
          isValid: false,
          message: 'Missing or invalid consumption data',
        });
      }

      // kVA demand validation
      if (typeof billData?.kvaDemand === 'number' && billData.kvaDemand >= 0) {
        results.push({
          field: 'kVA Demand',
          isValid: true,
          message: 'Valid kVA demand value',
        });
      } else {
        results.push({
          field: 'kVA Demand',
          isValid: false,
          message: 'Missing or invalid kVA demand',
        });
      }

      if (!this.usesCalculatedDemandCharge && typeof billData?.kvaDemandCharges === 'number' && billData.kvaDemandCharges >= 0) {
        results.push({
          field: 'kVA Demand Charges',
          isValid: true,
          message: 'Valid kVA demand charges provided',
        });
      } else if (!this.usesCalculatedDemandCharge && billData?.kvaDemandCharges !== undefined) {
        results.push({
          field: 'kVA Demand Charges',
          isValid: false,
          message: 'Missing or invalid kVA demand charges',
        });
      }

      // Calculated charges validation
      if (billData?.calculatedCharges && billData.calculatedCharges.totalCalculated > 0) {
        results.push({
          field: 'Calculated Charges',
          isValid: true,
          message: 'Bill calculation completed successfully',
        });
      } else {
        results.push({
          field: 'Calculated Charges',
          isValid: false,
          message: 'Missing or invalid calculated charges',
        });
      }

      // Actual bill amount validation
      if (typeof billData?.actualBillAmount === 'number' && billData.actualBillAmount >= 0) {
        results.push({
          field: 'Actual Bill Amount',
          isValid: true,
          message: 'Valid actual bill amount provided',
        });
      } else {
        results.push({
          field: 'Actual Bill Amount',
          isValid: false,
          message: 'Missing or invalid actual bill amount',
        });
      }

      return results;
    },

    isValidForSave() {
      return this.validationResults.every((result) => result.isValid);
    },

    firstInvalidMessage() {
      const failed = this.validationResults.find((r) => !r.isValid);
      return failed ? `${failed.field}: ${failed.message}` : '';
    },

    validationStatusColor() {
      return this.isValidForSave ? 'success' : 'error';
    },

    validationStatusIcon() {
      return this.isValidForSave ? 'mdi-check-circle' : 'mdi-alert-circle';
    },

    validationStatusIconColor() {
      return this.isValidForSave ? 'success' : 'error';
    },
  },

  methods: {
    ...mapActions('billing', ['saveBillingRecord', 'updateBillingRecord']),

    shouldSplitDemandByTouPeriod() {
      return appConfig.getBillingCapabilities().splitDemandByTouPeriod === true;
    },

    buildPersistedRecordData(source = this.currentBillData) {
      const billData = source || {};
      const manualData = this.editMode ? (this.manualData || {}) : {};
      const { isTimeOfUseMode } = this;
      const shouldPersistSplitDemand = isTimeOfUseMode && this.shouldSplitDemandByTouPeriod();
      const timeOfUseDemand = shouldPersistSplitDemand
        ? (manualData.timeOfUseDemand
          || billData.timeOfUseDemand
          || billData.consumptionData?.demandBreakdown
          || {})
        : {};
      const timeOfUseUnits = manualData.timeOfUseUnits
        || billData.timeOfUseUnits
        || billData.consumptionData?.touBreakdown
        || {};
      const kvaDemandSource = shouldPersistSplitDemand
        ? this.getTimeOfUseTotalDemand(timeOfUseDemand)
        : (manualData.kvaDemand ?? billData.kvaDemand);
      const kvaDemandChargesSource = shouldPersistSplitDemand
        ? this.getTimeOfUseDemandChargeTotal(timeOfUseDemand)
        : (manualData.kvaDemandCharges ?? billData.kvaDemandCharges);
      const actualBillAmountSource = manualData.actualBillAmount
        ?? billData.actualBillAmount;
      const actualKwhFromBillSource = manualData.actualKwhFromBill
        ?? billData.actualKwhFromBill;
      const totalConsumptionSource = isTimeOfUseMode
        ? this.getTimeOfUseTotalUnits(timeOfUseUnits) || billData.consumptionData?.totalConsumption
        : billData.consumptionData?.totalConsumption;
      const kvaDemand = parseFloat(kvaDemandSource);
      const kvaDemandCharges = parseFloat(kvaDemandChargesSource);
      const actualBillAmount = parseFloat(actualBillAmountSource);
      const actualKwhFromBill = parseFloat(actualKwhFromBillSource);
      const totalConsumption = parseFloat(totalConsumptionSource);
      const persistedKvaDemandCharges = Number.isNaN(kvaDemandCharges)
        ? 0
        : kvaDemandCharges;
      const calculatedCharges = billData.calculatedCharges || {};
      const unitCostForBilling = Number.isNaN(parseFloat(billData.unitCostForBilling))
        ? 0
        : parseFloat(billData.unitCostForBilling);
      const actualBillAmountValue = Number.isNaN(actualBillAmount) ? 0 : actualBillAmount;
      const variance = typeof billData.variance === 'number'
        ? billData.variance
        : actualBillAmountValue - (parseFloat(calculatedCharges.totalCalculated) || 0);
      const oilSurchargeRate = manualData.oilSurchargeRate ?? billData.oilSurchargeRate ?? 0;
      const serviceCharge = billData.serviceCharge
        || billData.calculatedCharges?.SERVICE_CHARGE
        || billData.calculatedCharges?.serviceCharges || 0;

      return {
        id: billData.id,
        _id: billData._id,
        assetid: billData.assetid
          ?? billData.assetId
          ?? this.originalRecord?.assetid
          ?? this.originalRecord?.assetId
          ?? 0,
        billingPeriod: billData.billingPeriod,
        consumptionData: {
          totalConsumption: Number.isNaN(totalConsumption) ? 0 : totalConsumption,
          initialReading: billData.consumptionData?.initialReading,
          finalReading: billData.consumptionData?.finalReading,
          billingPeriod: billData.consumptionData?.billingPeriod || billData.billingPeriod,
          touBreakdown: timeOfUseUnits,
          demandBreakdown: timeOfUseDemand,
        },
        kvaDemand: Number.isNaN(kvaDemand) ? 0 : kvaDemand,
        kvaDemandCharges: this.usesCalculatedDemandCharge && !isTimeOfUseMode
          ? 0
          : persistedKvaDemandCharges,
        calculatedCharges,
        actualBillAmount: actualBillAmountValue,
        actualKwhFromBill: Number.isNaN(actualKwhFromBill) ? 0 : actualKwhFromBill,
        timeOfUseUnits,
        timeOfUseDemand,
        oilSurchargeRate,
        serviceCharge,
        unitCostForBilling: this.shouldUseBillingUnitCost ? unitCostForBilling : 0,
        calculationMode: billData.calculationMode,
        variance,
        ratesUsed: billData.ratesUsed || [],
        notes: manualData.notes ?? billData.notes ?? '',
        createdBy: billData.createdBy
          || this.originalRecord?.createdBy
          || appConfig.getParentUserName(),
        updatedAt: billData.updatedAt || new Date(),
      };
    },

    getTimeOfUseTotalDemand(demand = {}) {
      return Object.values(demand || {}).reduce((sum, line) => (
        sum + (parseFloat(line?.kw) || 0)
      ), 0);
    },

    getTimeOfUseTotalUnits(units = {}) {
      return Object.values(units || {}).reduce((sum, line) => (
        sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0)
      ), 0);
    },

    getTimeOfUseDemandChargeTotal(demand = {}) {
      return Object.values(demand || {}).reduce((sum, line) => {
        const kw = Math.max(parseFloat(line?.kw) || 0, 0);
        const rate = parseFloat(line?.rate) || 0;
        return sum + (kw * rate);
      }, 0);
    },

    calculateDisplayedVariance() {
      const billData = this.currentBillData || {};
      const actualBillAmount = parseFloat(billData?.actualBillAmount) || 0;
      if (!this.shouldUseBillingUnitCost) {
        return actualBillAmount - (parseFloat(billData?.calculatedCharges?.totalCalculated) || 0);
      }

      return actualBillAmount - calculateTimeOfUseTotalCalculated(billData);
    },

    handleSaveClick() {
      this.handleSave();
    },

    async handleSave() {
      if (!this.isValidForSave) {
        this.error = 'Cannot save: Data validation failed';
        return;
      }

      this.saving = true;
      this.error = null;

      try {
        let savedRecord;

        if (this.editMode) {
          // Update existing record
          const recordId = this.originalRecord._id || this.originalRecord.id;
          const recordData = this.buildPersistedRecordData(this.updatedRecord
          || this.currentBillData);
          savedRecord = await this.updateBillingRecord({
            id: recordId,
            recordData,
          });

          this.$emit('save-success', savedRecord);
          this.$emit('show-message', {
            type: 'success',
            message: 'Billing record updated successfully',
          });
        } else {
          // Create new record
          const recordData = this.buildPersistedRecordData(this.billData);

          // Additional validation before saving - only for manual mode
          // In automatic mode, initialReading and finalReading are null, so skip this validation
          const isManualMode = recordData.consumptionData.initialReading !== null
                              && recordData.consumptionData.finalReading !== null;

          if (isManualMode
          && recordData.consumptionData.finalReading
          < recordData.consumptionData.initialReading) {
            throw new Error('Final reading must be greater than or equal to initial reading');
          }

          savedRecord = await this.saveBillingRecord(recordData);

          this.$emit('record-saved', savedRecord);
          this.$emit('show-message', {
            type: 'success',
            message: `Billing record saved successfully with ID: ${savedRecord.id}`,
          });
        }

        this.savedRecordId = savedRecord.id || savedRecord._id;
        this.saveTime = new Date();
        this.error = null;
        this.saved = true;
      } catch (error) {
        console.error('Error saving billing record:', error);
        this.error = error.message || 'Failed to save billing record';

        this.$emit('save-error', error);
        this.$emit('show-message', {
          type: 'error',
          message: this.error,
        });
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$emit('save-cancelled');
    },

    viewRecord() {
      if (this.savedRecordId) {
        this.$emit('view-record', this.savedRecordId);
      }
    },

    createNew() {
      this.$emit('create-new-bill');
    },

    // Public method to reset the component
    reset() {
      this.saving = false;
      this.saved = false;
      this.savedRecordId = null;
      this.saveTime = null;
      this.error = null;
    },
  },
};
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal !important;
}
</style>
